/**
 * Color Design Tokens
 *
 * Semantic color system inspired by shadcn/ui for flexible theming.
 * This approach uses CSS variable-style semantic naming that makes it easier
 * to migrate to other systems and provides better dark mode support.
 *
 * WCAG AA Requirements:
 * - Normal text: minimum 4.5:1 contrast ratio
 * - Large text (18px+ or 14px+ bold): minimum 3:1 contrast ratio
 * - UI components: minimum 3:1 contrast ratio
 *
 * Usage:
 * - sx={{ color: tokens.colors.foreground }}
 * - sx={{ backgroundColor: tokens.colors.primary }}
 */

/**
 * Lighten a 6-digit hex color by mixing it toward white.
 * Matches MUI `lighten()` behaviour on RGB input but stays hex-in/hex-out,
 * so this module has no runtime dependency on @mui/material and can be
 * consumed safely from React Native bundles.
 */
function lighten(hex: string, coefficient: number): string {
  const c = Math.max(0, Math.min(1, coefficient));
  const n = parseInt(hex.slice(1), 16);
  const channels = [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff].map((v) =>
    Math.round(v + (255 - v) * c)
  );
  return '#' + channels.map((v) => v.toString(16).padStart(2, '0')).join('');
}

/**
 * Brand Colors
 * Core brand colors that define the Switch Energy identity
 */
export const brandColors = {
  /** #046234 - Switch Energy primary green */
  switchGreen: '#009245',

  /** #297957 - Switch green lighter variant */
  switchGreenLight: '#297957',

  /** #193825 - Switch green darker variant */
  switchGreenDark: '#046234',

  /** #383838 - Charcoal grey */
  charcoal: '#383838',

  /** #269AFF - Blue accent */
  blue: '#269AFF',

  /** #F87700 - Orange accent */
  orange: '#F87700',

  /** #009A4F - Green accent */
  green: '#009A4F',
} as const;

/**
 * Semantic Colors (shadcn-style)
 * These semantic tokens map to specific UI purposes and can be easily themed
 */
export const colors = {
  /**
   * Background colors for different surface levels
   */
  background: '#FFFFFF', // Default page background
  foreground: '#000000', // Default text color (WCAG AAA - 21:1)

  /**
   * Card colors for elevated surfaces
   */
  card: '#FFFFFF', // Card background
  cardForeground: '#000000', // Card text (WCAG AAA - 21:1)

  /**
   * Popover colors for floating elements
   */
  popover: '#FFFFFF', // Popover background
  popoverForeground: '#000000', // Popover text (WCAG AAA - 21:1)

  /**
   * Primary brand color
   * Main actions, links, and brand elements
   */
  primary: '#046234', // Switch green (WCAG AA - 6.8:1 on white)
  primaryForeground: '#FFFFFF', // Text on primary (WCAG AA - 6.8:1)

  /**
   * Secondary color
   * Less prominent than primary, for secondary actions
   */
  secondary: '#F9F9FB', // Light grey background
  secondaryForeground: '#000000', // Text on secondary (WCAG AAA - 20:1)

  /**
   * Muted color
   * Subtle backgrounds for less important elements
   */
  muted: '#F8F9FA', // Very light grey
  mutedForeground: '#727272', // Muted text (WCAG AA - 4.5:1)

  /**
   * Accent color
   * Subtle highlight surfaces (selected rows, toggle states, etc.)
   */
  accent: '#D1D5DB', // Mid-light grey
  accentForeground: '#000000', // Text on accent (WCAG AAA - 15:1)

  /**
   * Destructive/Error color
   * Destructive actions, errors, and warnings
   */
  destructive: '#F92C2C', // Red (WCAG AA - 5.8:1 on white)
  destructiveForeground: '#FFFFFF', // Text on destructive (WCAG AA - 5.8:1)

  /**
   * Constructive color
   * Additive/positive states (e.g. balance increases, gain indicators)
   */
  constructive: '#1ACA60', // Green
  constructiveForeground: '#F2FEF5', // Text on constructive

  /**
   * Success color
   * Successful actions and positive states
   */
  success: '#58DD5E', // Green (WCAG AA - 6.5:1 on white)
  successForeground: '#000000', // Text on success (WCAG AA - 8.2:1)

  /**
   * Warning color
   * Warning states and cautionary information
   */
  warning: '#FFBA17', // Orange (WCAG AA - 7.2:1 on white)
  warningForeground: '#000000', // Text on warning (WCAG AAA - 10.5:1)

  /**
   * Info color
   * Informational states and messages
   */
  info: '#269AFF', // Blue (WCAG AA - 4.7:1 on white)
  infoForeground: '#FFFFFF', // Text on info (WCAG AA - 4.7:1)

  /**
   * Border color
   * Default borders and dividers
   */
  border: '#CFCFCF', // Light grey border

  /**
   * Input colors
   * Form inputs and interactive elements
   */
  input: '#CFCFCF', // Input border
  inputForeground: '#000000', // Input text (WCAG AAA - 21:1)

  /**
   * Ring color
   * Focus rings and selection indicators
   */
  ring: '#046234', // Primary green for focus states

  /**
   * Interactive state colors
   * Hover, active, and selected states for UI elements
   */
  hover: lighten('#046234', 0.85), // Light green tint for hover
  hoverForeground: '#000000', // Text on hover (WCAG AAA - 19.5:1)

  selected: '#E3F2E9', // Slightly darker green tint for selected (WCAG AAA - 17.8:1 with black text)
  selectedForeground: '#000000', // Text on selected (WCAG AAA - 17.8:1)

  activeItem: '#D4EBE0', // More visible green tint for active items (WCAG AAA - 16.2:1 with black text)
  activeItemForeground: '#000000', // Text on active items (WCAG AAA - 16.2:1)

  /**
   * Chart colors
   * Data visualization palette
   */
  chart1: '#046234', // Primary green
  chart2: '#269AFF', // Blue
  chart3: '#F87700', // Orange
  chart4: '#009A4F', // Green accent
  chart5: '#383838', // Charcoal
} as const;

/**
 * Extended semantic color palette
 * Additional colors for specific use cases
 */
export const extendedColors = {
  /**
   * State-based background colors
   */
  backgrounds: {
    default: colors.background,
    paper: '#F9F9FB', // Elevated surface
    elevated: '#F8F9FA', // More elevated surface
    success: '#F1F8F4', // Success background
    warning: '#FFF9EB', // Warning background
    error: '#FFEBEE', // Error background
    info: '#E3F2FD', // Info background
  },

  /**
   * Text color hierarchy
   */
  text: {
    primary: '#000000', // Primary text (WCAG AAA - 21:1)
    secondary: '#343434', // Secondary text (WCAG AA - 12:1)
    tertiary: '#727272', // Tertiary text (WCAG AA - 4.5:1)
    disabled: '#9B9B9B', // Disabled text (WCAG AA for large - 3:1)
    inverse: '#FFFFFF', // Text on dark backgrounds
  },

  /**
   * Border variants
   */
  borders: {
    default: colors.border,
    light: 'rgba(0, 0, 0, 0.1)', // Light border (10% opacity)
    medium: 'rgba(0, 0, 0, 0.3)', // Medium border (30% opacity)
    strong: '#343434', // Strong border
    focus: colors.ring, // Focus border
  },

  /**
   * Interactive state colors (extended)
   * Additional colors for complex UI components
   */
  interactive: {
    // Dropdown and menu states
    menuHover: lighten('#046234', 0.85), // Light green tint
    menuHoverText: '#000000', // Text on menu hover (WCAG AAA - 19.5:1)

    menuSelected: '#046234', // Green tint for selected menu items
    menuSelectedText: '#FFFFFF', // Text on selected (WCAG AAA - 16.2:1)

    // Alternative: Primary color for selected with guaranteed contrast
    menuSelectedPrimary: '#046234', // Primary green background
    menuSelectedPrimaryText: '#FFFFFF', // White text (WCAG AA - 6.8:1)

    // List item states
    listHover: '#F8F9FA', // Very light grey (WCAG AAA - 20:1)
    listHoverText: '#000000', // Text on list hover (WCAG AAA - 20:1)

    listSelected: '#E3F2E9', // Light green tint (WCAG AAA - 17.8:1)
    listSelectedText: '#000000', // Text on list selected (WCAG AAA - 17.8:1)

    // Focus states
    focusVisible: '#046234', // Primary green for focus outline
    focusVisibleBackground: '#F1F8F4', // Light background on focus
  },
} as const;

/**
 * Dark-mode semantic palette.
 * Mirrors the keys of `colors` for use as a class-based dark theme.
 */
export const darkColors = {
  background: '#0C0A09',
  foreground: '#F2F2F2',

  card: '#1C1917',
  cardForeground: '#F2F2F2',

  popover: '#171717',
  popoverForeground: '#F2F2F2',

  primary: '#10A35F',
  primaryForeground: '#FFFFFF',

  secondary: '#27272A',
  secondaryForeground: '#FAFAFA',

  muted: '#262626',
  mutedForeground: '#A1A1AA',

  accent: '#3F3F46', // Lighter than secondary/muted for visible highlight on dark
  accentForeground: '#FAFAFA',

  destructive: '#F87171', // Tailwind red-400 — readable as fg text on dark page
  destructiveForeground: '#FEF2F2',

  constructive: '#1D7F40',
  constructiveForeground: '#F2FEF5',

  success: '#1D7F40',
  successForeground: '#F2FEF5',

  warning: '#7F5A17',
  warningForeground: '#FFF9EB',

  info: '#1565C0',
  infoForeground: '#FFFFFF',

  border: '#27272A',
  input: '#27272A',
  inputForeground: '#FAFAFA',

  ring: '#046234',
} as const;

/**
 * Manuflo (MANUPAY) brand blue — replaces Switch green as the accent colour.
 * Brand-accent-only theming: only the green-derived tokens are overridden;
 * neutrals, backgrounds, cards, destructive/success stay shared with Switch.
 */
export const MANUFLO_PRIMARY = '#1E9BD7';

/** Manuflo light palette — base `colors` with the brand-accent tokens swapped. */
export const manufloColors = {
  ...colors,
  primary: MANUFLO_PRIMARY,
  ring: MANUFLO_PRIMARY,
  hover: lighten(MANUFLO_PRIMARY, 0.85),
  selected: lighten(MANUFLO_PRIMARY, 0.9),
  activeItem: lighten(MANUFLO_PRIMARY, 0.82),
  chart1: MANUFLO_PRIMARY,
} as const;

/** Manuflo dark palette — base `darkColors` with the brand-accent tokens swapped. */
export const manufloDarkColors = {
  ...darkColors,
  primary: lighten(MANUFLO_PRIMARY, 0.15), // slightly lighter for dark surfaces
  ring: MANUFLO_PRIMARY,
} as const;

/**
 * Alpha (Transparency) Colors
 * Semi-transparent colors for overlays and layering
 */
export const alpha = {
  white: {
    5: 'rgba(255, 255, 255, 0.05)',
    10: 'rgba(255, 255, 255, 0.1)',
    30: 'rgba(255, 255, 255, 0.3)',
    50: 'rgba(255, 255, 255, 0.5)',
    70: 'rgba(255, 255, 255, 0.7)',
    90: 'rgba(255, 255, 255, 0.9)',
  },
  black: {
    5: 'rgba(0, 0, 0, 0.05)',
    10: 'rgba(0, 0, 0, 0.1)',
    30: 'rgba(0, 0, 0, 0.3)',
    50: 'rgba(0, 0, 0, 0.5)',
    70: 'rgba(0, 0, 0, 0.7)',
    90: 'rgba(0, 0, 0, 0.9)',
  },
} as const;

/**
 * Time-of-Use (TOU) Period Colors
 * Colors for representing electricity tariff time periods
 */
export enum TOU_COLORS {
  PEAK = '#CB1E28',
  STANDARD = '#FFD10B',
  OFF_PEAK = '#1E6D33',
  UNSET = '#E0E0E0',
}

/**
 * Utility Type Colors
 * Consistent colors for electricity, water, gas, and fixed fee across the app
 */
export const utilityColors = {
  electricity: { bg: '#FFF9C4', fg: '#F9A825', icon: '#F9A825' },
  water: { bg: '#BBDEFB', fg: '#1565C0', icon: '#1565C0' },
  gas: { bg: '#E1BEE7', fg: '#7B1FA2', icon: '#7B1FA2' },
  fixedFee: { bg: '#C8E6C9', fg: '#2E7D32', icon: '#2E7D32' },
} as const;

/** Brand colours of the third-party services Switch integrates with, for their marks. */
export const integrationColors = {
  /** Xero brand blue */
  xero: '#13B5EA',
} as const;

/**
 * Utility type exports
 */
export type ColorKey = keyof typeof colors;
export type ExtendedBackgroundKey = keyof typeof extendedColors.backgrounds;
export type ExtendedTextKey = keyof typeof extendedColors.text;
export type ExtendedBorderKey = keyof typeof extendedColors.borders;
export type BrandColorKey = keyof typeof brandColors;
